import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Combobox, type ComboboxOption } from '@knack/asterisk-react';
import { snakeCase } from 'lodash';

import { type KnackField } from '@/types/schema/KnackField';
import { type KnackOperator } from '@/types/schema/KnackOperator';
import { type SearchViewFilters, type SearchViewInput } from '@/types/schema/views/SearchView';
import { useFieldHelpers } from '@/hooks/helpers/useFieldHelpers';
import { shouldHideValueBasedOnOperator } from '@/utils/field-operators';

type SearchFieldOperatorsProps = {
  input: SearchViewInput;
  criteriaField: KnackField;
  filterRuleIndex: number;
};

export function SearchFieldOperators({
  input,
  criteriaField,
  filterRuleIndex
}: SearchFieldOperatorsProps) {
  const [t] = useTranslation();

  const { control, getValues, setValue, clearErrors } = useFormContext<SearchViewFilters>();
  const { getBaseFieldOperators } = useFieldHelpers();

  const operators = useMemo(() => {
    if (!criteriaField) {
      return [];
    }

    const baseOperators = getBaseFieldOperators({ field: criteriaField });

    if (!baseOperators.includes('is any')) {
      baseOperators.push('is any');
    }

    return baseOperators;
  }, [criteriaField, getBaseFieldOperators]);

  const operatorOptions: ComboboxOption[] = useMemo(
    () =>
      operators.map((operator) => ({
        key: operator,
        label: t(`operators.${snakeCase(operator)}`)
      })),
    [operators, t]
  );

  return (
    <div>
      <Controller
        control={control}
        name={`rules.${filterRuleIndex}.operator`}
        render={({ field }) => {
          const selectedOption =
            operatorOptions.find((option) => option.key === field.value) || operatorOptions[0];

          return (
            <Combobox
              id={`${input.id}-operator`}
              triggerClassName="h-9 w-full sm:w-[140px]" // TODO: Remove height (h-9) - should be driven by Asterisk
              triggerTestId={`operator-select-${filterRuleIndex}`}
              contentClassName="min-w-[200px]"
              options={operatorOptions}
              selectedOption={selectedOption}
              onSelectOption={(newOption) => {
                const newOperator = newOption.key as KnackOperator;
                const criteriaToEdit = getValues(`rules.${filterRuleIndex}`);

                setValue(
                  `rules.${filterRuleIndex}`,
                  {
                    ...criteriaToEdit,
                    value: shouldHideValueBasedOnOperator(newOperator)
                      ? ''
                      : criteriaToEdit.value || input.value,
                    operator: newOperator
                  },
                  { shouldDirty: true }
                );

                clearErrors();
              }}
            />
          );
        }}
      />
    </div>
  );
}
